import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { SECTIONS, META_FIELDS, PARTS } from "@/data/workbook";
import { overallProgress } from "@/lib/progress";
import { Button } from "@/components/ui/button";
import { Loader2, Printer, Download, ArrowLeft, Check, X } from "lucide-react";
import { toast } from "sonner";

const isFilled = (v) => v !== undefined && v !== null && v !== "";

function CheckRow({ label, done }) {
  return (
    <div className="flex items-start gap-2 py-0.5 text-sm">
      {done ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500 print-accent" /> : <X className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground opacity-40" />}
      <span className={done ? "text-foreground" : "text-muted-foreground"}>{label}</span>
    </div>
  );
}

function BlockView({ block, data, photos }) {
  const v = block.key ? data?.[block.key] : undefined;

  if (block.type === "note") return null;

  if (block.type === "checklist") {
    const done = block.items.filter((it) => v?.[it.k]).length;
    return (
      <div className="mb-4 print-avoid-break">
        {block.title && <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{block.title} <span className="font-mono">({done}/{block.items.length})</span></h4>}
        <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
          {block.items.map((it) => <CheckRow key={it.k} label={it.label} done={!!v?.[it.k]} />)}
        </div>
      </div>
    );
  }

  if (block.type === "wmc") {
    return (
      <div className="mb-4 print-avoid-break">
        {block.title && <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{block.title}</h4>}
        {block.items.map((it) => {
          const s = v?.[it.k] || {};
          const tags = ["w", "m", "c"].filter((x) => s[x]).map((x) => x.toUpperCase());
          return (
            <div key={it.k} className="flex items-center justify-between gap-3 py-0.5 text-sm">
              <span className={tags.length ? "text-foreground" : "text-muted-foreground"}>{it.label}</span>
              <span className="font-mono text-xs text-primary print-accent">{tags.length ? tags.join(" ") : "—"}</span>
            </div>
          );
        })}
      </div>
    );
  }

  if (block.type === "fields") {
    const filled = block.fields.filter((f) => isFilled(v?.[f.k]));
    if (filled.length === 0) return null;
    return (
      <div className="mb-4 print-avoid-break">
        {block.title && <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{block.title}</h4>}
        <div className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
          {filled.map((f) => (
            <div key={f.k} className="flex gap-2 border-b border-border py-1 text-sm">
              <span className="min-w-[40%] text-muted-foreground">{f.label}</span>
              <span className="font-medium text-foreground">{v[f.k]}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (block.type === "measurements") {
    const filled = block.rows.filter((r) => isFilled(v?.[r.k]?.value));
    if (filled.length === 0) return null;
    return (
      <div className="mb-4 print-avoid-break">
        {block.title && <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{block.title}</h4>}
        <table className="w-full border-collapse text-sm">
          <tbody>
            {filled.map((r) => {
              const d = v[r.k];
              return (
                <tr key={r.k} className="border-b border-border">
                  <td className="py-1 pr-3 text-muted-foreground">{r.label}</td>
                  <td className="py-1 text-right font-mono font-medium text-foreground">{d.value} {d.unit || (r.unit !== "" ? r.unit : "")}</td>
                  <td className="py-1 pl-3 text-right font-mono text-xs text-primary print-accent">{d.photo || ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  if (block.type === "table") {
    const rows = Array.isArray(v) ? v.filter((r) => Object.values(r).some(isFilled)) : [];
    if (rows.length === 0) return null;
    return (
      <div className="mb-4 print-avoid-break">
        {block.title && <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{block.title}</h4>}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b-2 border-border">
                {block.columns.map((c) => <th key={c.k} className="px-2 py-1 text-left font-semibold uppercase tracking-wider text-muted-foreground">{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="border-b border-border">
                  {block.columns.map((c) => <td key={c.k} className="px-2 py-1 text-foreground">{row[c.k] || ""}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (block.type === "textarea") {
    if (!isFilled(v)) return null;
    return (
      <div className="mb-4 print-avoid-break">
        <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{block.label}</h4>
        <p className="whitespace-pre-wrap rounded border border-border bg-secondary/20 p-2 text-sm text-foreground">{v}</p>
      </div>
    );
  }

  if (block.type === "photoRegister") {
    if (photos.length === 0) return <p className="text-sm text-muted-foreground">No photos recorded.</p>;
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {photos.map((p) => (
          <div key={p.id} className="print-avoid-break overflow-hidden rounded-lg border border-border">
            <img src={fileUrl(p.storage_path)} alt={p.photo_id} className="aspect-square w-full object-cover" />
            <div className="p-1.5">
              <p className="font-mono text-[11px] font-bold text-primary print-accent">{p.photo_id}{p.view ? ` · ${p.view}` : ""}</p>
              <p className="text-[11px] text-foreground">{p.area}</p>
              {p.notes && <p className="text-[10px] text-muted-foreground">{p.notes}</p>}
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
}

export default function Report() {
  const { id } = useParams();
  const nav = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [pdfBusy, setPdfBusy] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    Promise.all([api.getSurvey(id), api.listPhotos(id)])
      .then(([s, p]) => { setSurvey(s); setPhotos(p); })
      .catch(() => nav("/"));
  }, [id, nav]);

  const downloadPdf = async () => {
    const el = contentRef.current;
    if (!el) return;
    setPdfBusy(true);
    el.classList.add("pdf-mode");
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      const filename = `${(survey.title || "survey").replace(/[^a-z0-9]+/gi, "_")}_technical_survey.pdf`;
      await html2pdf().set({
        margin: [10, 10, 12, 10],
        filename,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      }).from(el).save();
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF export failed");
    } finally {
      el.classList.remove("pdf-mode");
      setPdfBusy(false);
    }
  };

  if (!survey) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const filledMeta = META_FIELDS.filter((f) => isFilled(survey.meta?.[f.k]));

  return (
    <div className="relative z-10 min-h-screen report-root">
      {/* Toolbar */}
      <div className="sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-border bg-background/90 px-4 py-3 backdrop-blur-xl no-print sm:px-6">
        <Button variant="ghost" size="sm" onClick={() => nav(`/survey/${id}`)} className="gap-2" data-testid="report-back"><ArrowLeft className="h-4 w-4" /> Back to workbook</Button>
        <div className="flex items-center gap-2">
          <Button data-testid="print-report-btn" variant="outline" onClick={() => window.print()} className="gap-2 tap-scale">
            <Printer className="h-4 w-4" /> <span className="hidden sm:inline">Print</span>
          </Button>
          <Button data-testid="download-pdf-btn" onClick={downloadPdf} disabled={pdfBusy} className="gap-2 bg-primary text-primary-foreground tap-scale hover:bg-primary/90">
            {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
          </Button>
        </div>
      </div>

      <div ref={contentRef} className="mx-auto max-w-4xl px-5 py-8 sm:px-10 sm:py-12">
        {/* Cover */}
        <div className="mb-10 border-b-2 border-border pb-8 print-avoid-break">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary print-accent">Technical Site Walk-Through & As-Built Survey</span>
          <h1 className="mt-2 font-mono text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">{survey.title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">Field workbook · venue technical reference audit</p>
          {filledMeta.length > 0 && (
            <div className="mt-6 grid grid-cols-2 gap-x-8 gap-y-2 sm:grid-cols-3">
              {filledMeta.map((f) => (
                <div key={f.k}>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{f.label}</div>
                  <div className="font-medium text-foreground">{survey.meta[f.k]}</div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-border px-3 py-1 font-mono text-xs">
            Overall completion: <span className="font-bold text-primary print-accent">{overallProgress(survey.data, photos.length)}%</span>
            <span className="text-muted-foreground">· {photos.length} photos</span>
          </div>
        </div>

        {/* Parts & Sections */}
        {PARTS.map((part) => {
          const sections = SECTIONS.filter((s) => s.part === part.id);
          return (
            <div key={part.id} className="print-page-break">
              <div className="mb-6 mt-2 flex items-center gap-3 border-b border-border pb-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md font-mono text-sm font-bold" style={{ background: part.color, color: "#0B0F17" }}>{part.id}</span>
                <h2 className="font-mono text-xl font-bold uppercase tracking-wide text-foreground">Part {part.id} — {part.title}</h2>
              </div>
              {sections.map((s) => (
                <section key={s.id} className="mb-8">
                  <h3 className="mb-3 flex items-baseline gap-2 text-lg font-bold text-foreground">
                    <span className="font-mono text-sm text-primary print-accent">{s.num}</span> {s.title}
                  </h3>
                  {s.blocks.map((block, i) => (
                    <BlockView key={block.key || i} block={block} data={survey.data?.[s.id]} photos={photos} />
                  ))}
                </section>
              ))}
            </div>
          );
        })}

        <div className="mt-10 border-t border-border pt-4 text-center font-mono text-xs text-muted-foreground no-print">
          End of report · Generated {new Date().toLocaleString()}
        </div>
      </div>
    </div>
  );
}
